import { motion } from "framer-motion";
import { Sparkles, ShoppingBag, Compass, FileText, Users, ChefHat, ArrowRight, Map, Database, ShieldCheck, Target } from "lucide-react";
import { StatBar } from "@/components/StatBar";
import { MEDIA } from "@/lib/media";

function ExploreCard({ item, index }) {
  const Icon = item.icon;
  const inner = (
    <>
      <img src={item.img} alt={item.alt} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal/95 via-charcoal/50 to-charcoal/20" />
      <span className="absolute top-5 left-5 w-11 h-11 rounded-full border border-blaze/70 grid place-items-center text-blaze bg-charcoal/40 backdrop-blur-sm">
        <Icon size={20} strokeWidth={1.6} />
      </span>
      <div className="absolute inset-x-0 bottom-0 p-6">
        <h3 className="font-serif text-3xl text-bone leading-tight">{item.title}</h3>
        <p className="text-bone/75 text-sm mt-2 max-w-sm">{item.desc}</p>
        <ArrowRight size={22} className="text-blaze mt-4 transition-transform duration-300 group-hover:translate-x-1.5" />
      </div>
    </>
  );
  const cls = "group relative block h-[300px] md:h-[340px] overflow-hidden";
  const testid = `explore-card-${item.title.toLowerCase().replace(/[^a-z]+/g, "-")}`;
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.05 }}>
      {item.onClick ? (
        <button onClick={item.onClick} className={`${cls} w-full text-left`} data-testid={testid}>{inner}</button>
      ) : (
        <a href={item.href} className={cls} data-testid={testid}>{inner}</a>
      )}
    </motion.div>
  );
}

export function ExploreHub() {
  const cards = [
    { title: "AI Hunting Assistant", desc: "Personal guidance 24/7 for every step of your hunt.", icon: Sparkles, img: MEDIA.exploreAI, alt: "Hunter glassing a ridge at dusk", href: "/ai" },
    { title: "Gear & Marketplace", desc: "Compare. Shop. Save. From top brands and licensed sellers.", icon: ShoppingBag, img: MEDIA.exploreGear, alt: "Premium hunting optics on rock", href: "/gear" },
    { title: "Outfitters & Hunts", desc: "Discover outfitters and guided hunts that fit your goals.", icon: Compass, img: MEDIA.exploreOutfitters, alt: "Backcountry hunting camp at dusk", href: "/outfitters" },
    { title: "Regs & Licenses", desc: "Stay legal. Stay informed. Everything you need in one place.", icon: FileText, img: MEDIA.exploreRegs, alt: "Hand writing in a field journal with a map", href: "/explore" },
    { title: "Field Notes & Archive", desc: "Stories. Connection. Legacy. Keep yours and discover theirs.", icon: Users, img: MEDIA.exploreField, alt: "Hunters around a campfire at night", href: "/field-notes" },
    { title: "Recipes & Films", desc: "From camp to table. Recipes, films, and inspiration.", icon: ChefHat, img: MEDIA.exploreRecipes, alt: "Wild game cooking in a cast iron skillet", href: "/recipes" },
  ];

  const stats = [
    { icon: Map, value: "50+", label: "States Covered" },
    { icon: Target, value: "100+", label: "Species & Hunts" },
    { icon: Database, value: "1M+", label: "Data Points" },
    { icon: ShieldCheck, value: "Verified", label: "Official Sources" },
  ];

  return (
    <section className="bg-bone" data-testid="explore-hub">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-14">
        <span className="hr-overline text-blaze">Explore HomeRange</span>
        <h2 className="font-serif text-4xl md:text-6xl text-forest tracking-tight mt-3 max-w-2xl leading-none">Explore HomeRange</h2>
        <p className="text-base md:text-lg text-charcoal/70 mt-4 max-w-xl">Powerful tools and trusted resources — all in one place to help you plan, prepare, and succeed.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
          {cards.map((c, i) => <ExploreCard key={c.title} item={c} index={i} />)}
        </div>
      </div>
      <StatBar items={stats} className="border-t-0" />
    </section>
  );
}
